import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import TeambookTextForm from "../../../default_components/TeambookTextForm";
import Api from "../../../../Api";
import { TeambookIcon } from "../../../default_images/TeambookIcon";
import { icons } from "../../../default_images/IconsList";
import { DeleteTaskWarning } from "../lib/DeleteTaskWarning";
import { shallow } from "zustand/shallow";
import { useAccountStore } from "../../../../stores/accountStore";
import TeambookIconButton from "../../../default_components/TeambookIconButton";
import PropTypes from "prop-types";
import { TeambookCheckbox } from "../../../default_components/TeambookCheckbox";
import { DateTime } from "luxon";
import TeambookDatePicker from "../../../default_components/teambook_date_selector/DatePicker";

const ProjectAdvancedTab = ({
  tasks = [],
  setTasks = () => {},
  asyncCreate = false,
  project,
  updateProjectsList = () => {},
  customFields,
  setCustomFields = () => {},
  usersLimit,
  setUsersLimit = () => {},
  unavailableDates,
  setUnavailableDates = () => {},
}) => {
  const { t } = useTranslation();
  const [editTaskId, setEditTaskId] = useState(null);
  const [editTaskName, setEditTaskName] = useState("");

  const [isTaskCreating, setIsTaskCreating] = useState(false);
  const [creatingTaskName, setCreatingTaskName] = useState("");
  const [deleteConfirmationWindowOpened, setDeleteConfirmationWindowOpened] = useState(false);
  const [selectedTask, setSelectedTask] = useState();

  const [account] = useAccountStore((state) => [state.account], shallow);

  const [creatingTaskError, setCreatingTaskError] = useState(null);
  const [editingTaskError, setEditingTaskError] = useState(null);
  const [errorField, setErrorField] = useState(null);

  const taskRefs = useRef({});

  useEffect(() => {
    if (editingTaskError && taskRefs.current[editTaskId]) {
      taskRefs.current[editTaskId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [editingTaskError, editTaskId]);

  const startEditingTask = (id) => {
    cancelCreatingTask();
    setEditTaskId(id);
    setEditTaskName(tasks.find((task) => task.value === id || task.id === id).name);
  };

  const finishEditingTask = () => {
    if (editTaskName.length < 1 || editTaskName.length > 30) {
      setEditingTaskError(t("projects.error_message"));
      setErrorField("name");
      return;
    }

    setEditingTaskError(null);
    setErrorField(null);

    if (asyncCreate) {
      Api.Tasks.update(editTaskId, editTaskName).then(() => {
        setTasks((pr) =>
          pr.map((task) => {
            if (task.value === editTaskId) {
              return { ...task, name: editTaskName };
            } else {
              return { ...task };
            }
          })
        );
      });

      updateProjectsList();
    } else {
      setTasks((pr) =>
        pr.map((task) => {
          if (task.value === editTaskId) {
            return { value: editTaskName, name: editTaskName };
          } else {
            return { ...task };
          }
        })
      );
    }
    cancelEditingTask();
  };

  const changeUsersLimit = (newValue) => {
    if (newValue) {
      setUsersLimit({ ...usersLimit, limit: newValue });
    } else {
      setUsersLimit({ ...usersLimit, limit: null });
    }
  };

  const cancelEditingTask = () => {
    setEditTaskId(null);
    setEditTaskName("");
    setEditingTaskError(null);
    setErrorField(null);
  };

  const openConfirmationDelete = (task) => {
    setDeleteConfirmationWindowOpened(true);
    setSelectedTask(task);
  };

  const deleteTask = (task) => {
    openConfirmationDelete(task);
  };

  const startCreatingTask = () => {
    cancelEditingTask();
    setIsTaskCreating(true);
  };

  const cancelCreatingTask = () => {
    setIsTaskCreating(false);
    setCreatingTaskName("");
    setCreatingTaskError(null);
    setErrorField(null);
  };

  const finishCreatingTask = () => {
    if (creatingTaskName.length < 1 || creatingTaskName.length > 30) {
      setCreatingTaskError(t("projects.error_message"));
      setErrorField("name");
      return;
    }

    setCreatingTaskError(null);
    setErrorField(null);
    if (asyncCreate) {
      Api.Tasks.create({
        project_id: project.id,
        name: creatingTaskName,
      }).then((res) => {
        setTasks((pr) => [...pr, { id: res.data.id, value: res.data.id, name: res.data.name }]);
      });
      updateProjectsList();
    } else {
      setTasks((pr) => [...pr, { value: creatingTaskName, name: creatingTaskName }]);
    }
    cancelCreatingTask();
  };

  const addNewUnavailableDates = () => {
    const currentDates = unavailableDates ? [...unavailableDates] : [];
    setUnavailableDates([...currentDates, [DateTime.now().toISODate(), DateTime.now().plus({ days: 4 }).toISODate()]]);
  };

  const changeUnavailableDates = (date, dateIndex, rangeIndex) => {
    const newUnavailableDates = [...unavailableDates];
    newUnavailableDates[dateIndex][rangeIndex] = date.toISODate();
    setUnavailableDates(newUnavailableDates);
  };

  const removeUnavailableDates = (index) => {
    const newUnavailableDates = [...unavailableDates];
    newUnavailableDates.splice(index, 1);
    setUnavailableDates(newUnavailableDates);
  };

  return (
    <div className="project-form__tasks-tab">
      <div className="advanced-tab__setting">
        <p className="advanced-tab__setting__title">
          {t("projects.tasks")} <TeambookIcon onClick={startCreatingTask} name={icons.ADD} />
        </p>

        {!isTaskCreating && tasks.length === 0 && <p className="project-form__no-tasks">{t("projects.no_tasks")}</p>}

        {deleteConfirmationWindowOpened && (
          <DeleteTaskWarning
            close={() => setDeleteConfirmationWindowOpened(false)}
            task={selectedTask}
            onYes={(id) => {
              if (asyncCreate) {
                Api.Tasks.delete(id).then(() => {
                  setTasks((pr) => pr.filter((task) => task.value !== id));
                });
                updateProjectsList();
              } else {
                setTasks((pr) => pr.filter((task) => task.id !== id || task.value !== id));
              }
            }}
          />
        )}

        <div className="project-form__task-list">
          {isTaskCreating && (
            <div className="project-form__task" mode="create">
              <TeambookTextForm
                fieldValue={creatingTaskName}
                placeholder={t("projects.task_name")}
                onChange={setCreatingTaskName}
                errorField={errorField}
                errorMessage={creatingTaskError}
                field={"name"}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    finishCreatingTask();
                  }
                }}
              />

              <div className="project-form__task-actions">
                <div onClick={finishCreatingTask}>
                  <TeambookIconButton name={icons.CHECK} tooltipTitle={t("save")} />
                </div>
                <div onClick={cancelCreatingTask}>
                  <TeambookIconButton name={icons.CROSS} tooltipTitle={t("cancel")} />
                </div>
              </div>
            </div>
          )}

          {tasks.map((task) =>
            editTaskId === task.id || editTaskId === task.value ? (
              <>
                <div
                  className="project-form__task"
                  key={task.id || task.value}
                  ref={(el) => (taskRefs.current[task.id || task.value] = el)}
                >
                  <TeambookTextForm
                    placeholder={t("projects.task_name")}
                    fieldValue={editTaskName}
                    onChange={setEditTaskName}
                    errorField={errorField}
                    errorMessage={editingTaskError}
                    field={"name"}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        finishEditingTask();
                      }
                    }}
                  />

                  <div className="project-form__task-actions">
                    <div onClick={finishEditingTask}>
                      <TeambookIconButton name={icons.CHECK} isGray tooltipTitle={t("save")} />
                    </div>
                    <div onClick={cancelEditingTask}>
                      <TeambookIconButton name={icons.CROSS} isGray tooltipTitle={t("cancel")} />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="project-form__task" key={task.id || task.value}>
                <p>{task.name}</p>
                <div className="project-form__task-actions">
                  <div onClick={() => startEditingTask(task.id || task.value)}>
                    <TeambookIconButton name={icons.PENCIL} isGray tooltipTitle={t("edit")} />
                  </div>
                  <div onClick={() => deleteTask(task)}>
                    <TeambookIconButton name={icons.TRASH} isGray tooltipTitle={t("delete")} />
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <div className="advanced-tab__setting">
        <p className="advanced-tab__setting__title">{t("projects.limit_users_precheck")}</p>

        <div>
          <TeambookTextForm
            className="text-field"
            fieldName={"Users per day"}
            type={"number"}
            width={"30%"}
            onChange={changeUsersLimit}
            fieldValue={usersLimit.limit}
          />

          <div className="flex">
            <TeambookCheckbox
              checked={usersLimit.enforced}
              onChange={() => setUsersLimit({ ...usersLimit, enforced: !usersLimit.enforced })}
            />

            <p
              className="checkbox-label"
              style={{ margin: "2px 0 0 6px", cursor: "pointer" }}
              onClick={() => setUsersLimit({ ...usersLimit, enforced: !usersLimit.enforced })}
            >
              {t("projects.limit_users_enforce")}
            </p>
          </div>
        </div>
      </div>

      <div className="advanced-tab__setting">
        <p className="advanced-tab__setting__title">
          {t("projects.unavailable_dates_precheck")} <TeambookIcon onClick={addNewUnavailableDates} name={icons.ADD} pointer />
        </p>

        <div className="unavailable-dates-block">
          {unavailableDates?.map((dateRange, index) => (
            <div className="unavailable-dates" key={`unavailable-date-${index}`}>
              <TeambookDatePicker
                onDayClick={(date) => changeUnavailableDates(date, index, 0)}
                from={DateTime.fromISO(dateRange[0])}
                range={1}
                type="default"
                variant="borderedInline"
                formatStart={"d MMM, yyyy"}
                width={86}
                height={24}
                style={{ backgroundColor: "var(--background-1)" }}
              />
              -
              <TeambookDatePicker
                onDayClick={(date) => changeUnavailableDates(date, index, 1)}
                from={DateTime.fromISO(dateRange[1])}
                range={1}
                type="default"
                variant="borderedInline"
                formatStart={"d MMM, yyyy"}
                width={86}
                height={24}
                style={{ backgroundColor: "var(--background-1)" }}
              />
              <TeambookIcon
                name={icons.CROSS}
                className="unavailable-dates__cross"
                onClick={() => removeUnavailableDates(index)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="advanced-tab__setting">
        <p className="advanced-tab__setting__title">{t("projects.custom_fields")}</p>

        <div className="custom-fields-inputs">
          {account.custom_project_fields[0] && (
            <TeambookTextForm
              fieldName={account.custom_project_fields[0] || ""}
              onChange={(newValue) => setCustomFields({ ...customFields, field_value_1: newValue })}
              fieldValue={customFields.field_value_1 || ""}
              type={"text"}
              className={"custom-field__input"}
            />
          )}

          {account.custom_project_fields[1] && (
            <TeambookTextForm
              fieldName={account.custom_project_fields[1] || ""}
              onChange={(newValue) => setCustomFields({ ...customFields, field_value_2: newValue })}
              fieldValue={customFields.field_value_2 || ""}
              type={"text"}
              className={"custom-field__input"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

ProjectAdvancedTab.propTypes = {
  tasks: PropTypes.array.isRequired,
  setTasks: PropTypes.func.isRequired,
  asyncCreate: PropTypes.bool,
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  updateProjectsList: PropTypes.func.isRequired,
  customFields: PropTypes.shape({
    field_value_1: PropTypes.string,
    field_value_2: PropTypes.string,
  }),
  setCustomFields: PropTypes.func.isRequired,
  usersLimit: PropTypes.shape({
    limit: PropTypes.number,
    enforced: PropTypes.bool,
  }),
  setUsersLimit: PropTypes.func,
  unavailableDates: PropTypes.array,
  setUnavailableDates: PropTypes.func,
};

export default ProjectAdvancedTab;
